import React, { useState, useEffect } from 'react'
import styled from 'styled-components'
import Button from './Button'
import PointLog from './PointLog'
import theme, { getColorByTierName } from '../styles/theme'
import { depositPoints, spendPoints, subtractPoints, moveTier, getUserTimeline, POINT_SOURCES, TIER_LEVEL_IDS, depositOptInPoint, updateUserLoyaltyStatus, syncTierToMarianaTek } from '../services/api'
import { useAuditLog, AUDIT_ACTIONS } from '../services/auditLog'
import { useAuth } from './auth/AuthProvider'
import TierHistory from './TierHistory'
import UserNotesModal from './UserNotesModal'

const AcademyContainer = styled.div`
  background: ${theme.color.background.light};
  padding: 2rem;
  border-radius: 8px;
  box-shadow: inset 0 2px 4px ${theme.color.shadow.light};
`

const AcademyHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  gap: 2rem;
  flex-wrap: wrap;
`

const HeaderActions = styled.div`
  display: flex;
  gap: 1rem;
  align-items: center;
`

const UserNotesButton = styled(Button)`
  background: #fefacd;
  color: #444;
  border: 2px solid #d4a017;
  font-weight: 600;

  &:hover:not([disabled]) {
    background: #fef9b8;
    border-color: #b8860b;
  }
`

const AcademyMainContent = styled.div`
  flex: 1;
`

const Title = styled.h2`
  font-size: 2.4rem;
  font-weight: 700;
  color: ${theme.color.text.base};
  margin: 0;
`

const TierSection = styled.div`
  margin-bottom: 2rem;
`

const TierInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 2rem;
  margin-bottom: 1rem;
  flex-wrap: wrap;
`

const TierMoveButtons = styled.div`
  display: flex;
  gap: 0.5rem;
  align-items: center;
  flex-wrap: wrap;
`

const TierMoveButton = styled(Button)`
  padding: 0.5rem 1rem;
  font-size: 1.2rem;
  font-weight: 600;
  border-radius: 4px;
  
  &.promote {
    background: #28a745;
    color: white;
    
    &:hover:not([disabled]) {
      background: #218838;
    }
  }
  
  &.demote {
    background: #ffc107;
    color: #212529;
    
    &:hover:not([disabled]) {
      background: #e0a800;
    }
  }
`

const CheckboxContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background: #f8f9fa;
  border-radius: 4px;
  border: 1px solid ${theme.color.border.light};
`

const Checkbox = styled.input`
  width: 16px;
  height: 16px;
  cursor: pointer;
`

const CheckboxLabel = styled.label`
  font-size: 1.2rem;
  color: ${theme.color.text.base};
  cursor: pointer;
  font-weight: 500;
  white-space: nowrap;
`

const TierBadge = styled.div<{ tier: string }>`
  padding: 0.5rem 1rem;
  border-radius: 4px;
  background: ${props => getColorByTierName(props.tier, theme.color.background.lightGrey)};
  color: ${props => props.tier.toLowerCase() === 'member' ? theme.color.text.base : theme.color.text.light};
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`

const PointsInfo = styled.div`
  font-size: 1.8rem;
  font-weight: 600;
  color: ${theme.color.text.base};
`

const ProgressContainer = styled.div`
  margin-bottom: 2rem;
`

const ProgressLabel = styled.div`
  margin-bottom: 0.5rem;
  font-size: 1.4rem;
  color: ${theme.color.text.grey};
  font-weight: 500;
`

const ProgressBar = styled.div`
  width: 100%;
  height: 12px;
  background: ${theme.color.background.lightGrey};
  border-radius: 8px;
  overflow: hidden;
  box-shadow: inset 0 2px 4px ${theme.color.shadow.light};
  border: 1px solid ${theme.color.border.light};
`

const ProgressFill = styled.div<{ progress: number }>`
  height: 100%;
  width: ${props => props.progress}%;
  background: linear-gradient(90deg, 
    #dc3545, 
    #dc3545dd
  );
  transition: width 1.5s cubic-bezier(0.4, 0.0, 0.2, 1);
  box-shadow: 0 1px 3px ${theme.color.shadow.light};
  border-radius: 6px;
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 50%;
    background: linear-gradient(to bottom, rgba(255,255,255,0.3), transparent);
    border-radius: 6px 6px 0 0;
  }
`

const ActionsSection = styled.div`
  border-top: 1px solid ${theme.color.border.light};
  padding-top: 2rem;
`

const ActionsTitle = styled.h3`
  font-size: 1.8rem;
  font-weight: 600;
  color: ${theme.color.text.base};
  margin-bottom: 1rem;
`

const ActionsForm = styled.form`
  display: flex;
  gap: 1rem;
  align-items: end;
  flex-wrap: wrap;
`

const EnrollButton = styled.button`
  background: linear-gradient(135deg, #ff4757 0%, #ff3742 100%);
  color: white;
  border: none;
  border-radius: 50px;
  padding: 1.5rem 3rem;
  font-size: 1.6rem;
  font-weight: 700;
  cursor: pointer;
  position: relative;
  box-shadow: 
    0 8px 16px rgba(255, 71, 87, 0.3),
    inset 0 1px 0 rgba(255, 255, 255, 0.2),
    inset 0 -1px 0 rgba(0, 0, 0, 0.1);
  transform: translateY(0);
  transition: all 0.2s ease;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin: 1rem 0;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 
      0 12px 24px rgba(255, 71, 87, 0.4),
      inset 0 1px 0 rgba(255, 255, 255, 0.3),
      inset 0 -1px 0 rgba(0, 0, 0, 0.1);
  }
  
  &:active {
    transform: translateY(1px);
    box-shadow: 
      0 4px 8px rgba(255, 71, 87, 0.3),
      inset 0 1px 0 rgba(255, 255, 255, 0.1),
      inset 0 -1px 0 rgba(0, 0, 0, 0.2);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: translateY(0);
  }
`

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  min-width: 150px;
`

const Label = styled.label`
  font-size: 1.2rem;
  font-weight: 600;
  color: ${theme.color.text.grey};
  text-transform: uppercase;
  letter-spacing: 0.05em;
`

const Select = styled.select`
  padding: 1rem;
  border: 2px solid ${theme.color.border.light};
  border-radius: 4px;
  font-size: 1.4rem;
  background: white;
  
  &:focus {
    outline: none;
    border-color: ${theme.color.accent.primary};
  }
`

const Input = styled.input`
  padding: 1rem;
  border: 2px solid ${theme.color.border.light};
  border-radius: 4px;
  font-size: 1.4rem;
  
  &:focus {
    outline: none;
    border-color: ${theme.color.accent.primary};
  }
`

const ActionButtons = styled.div`
  display: flex;
  gap: 1rem;
`

const GreenButton = styled(Button)`
  background: #28a745;
  color: white;
  
  &:hover:not([disabled]) {
    background: #218838;
  }
`

const RedButton = styled(Button)`
  background: #dc3545;
  color: white;
  
  &:hover:not([disabled]) {
    background: #c82333;
  }
`

const ErrorMessage = styled.div`
  background: #ffe6e6;
  color: #d70036;
  padding: 1rem;
  border-radius: 4px;
  margin-top: 1rem;
`

const SuccessMessage = styled.div`
  background: #e6ffe6;
  color: #007700;
  padding: 1rem;
  border-radius: 4px;
  margin-top: 1rem;
`

const TierCheckingMessage = styled.div`
  background: #fff3cd;
  color: #856404;
  padding: 1rem;
  border-radius: 4px;
  margin-top: 1rem;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  animation: pulse 2s infinite;
  
  @keyframes pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.7; }
  }
`

const SpinnerIcon = styled.div`
  width: 16px;
  height: 16px;
  border: 2px solid #856404;
  border-top: 2px solid transparent;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`

interface AcademySectionProps {
  userData: any
  onUpdate: () => void
  refreshTimestamp?: number
}

const AcademySection: React.FC<AcademySectionProps> = ({ userData, onUpdate, refreshTimestamp }) => {
  const { logEvent } = useAuditLog()
  const { isSuperAdmin, isAdmin } = useAuth()
  const [pointSource, setPointSource] = useState<string>('Customer Service')
  const [amount, setAmount] = useState<string>('')
  const [isLoading, setIsLoading] = useState(false)
  const [message, setMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null)
  const [animatedProgress, setAnimatedProgress] = useState(0)
  const [isCheckingTier, setIsCheckingTier] = useState(false)
  const [isTierMoving, setIsTierMoving] = useState(false)
  const [isEnrolling, setIsEnrolling] = useState(false)
  const [transactions, setTransactions] = useState<any[]>([])
  const [selectedTransaction, setSelectedTransaction] = useState<string>('')
  const [applyTierRewards, setApplyTierRewards] = useState(true)
  const [showUserNotes, setShowUserNotes] = useState(false)
  const [isSyncingTier, setIsSyncingTier] = useState(false)
  const loadingTransactionsRef = React.useRef(false)

  // Calculate points to next tier and progress
  const getNextTierInfo = () => {
    if (!userData || !userData.user) {
      return { nextTier: null, pointsToNext: 0, progress: 0 }
    }
    
    const user = userData.user
    const tier = user.tier || 'Member'
    const tierPoints = user.tier_points || 0
    
    const tierOrder = ['Member', 'Red', 'Silver', 'Black']
    const currentIndex = tierOrder.findIndex(t => t.toLowerCase() === tier.toLowerCase())
    
    if (currentIndex === -1 || currentIndex === tierOrder.length - 1) {
      return { nextTier: null, pointsToNext: 0, progress: 100 }
    }
    
    const tierThresholds = { Member: 0, Red: 1000, Silver: 2500, Black: 6000 }
    const nextTier = tierOrder[currentIndex + 1]
    const nextThreshold = tierThresholds[nextTier as keyof typeof tierThresholds] || 0
    const pointsToNext = nextThreshold - tierPoints
    const progress = (tierPoints / nextThreshold) * 100
    
    return { nextTier, pointsToNext: Math.max(0, pointsToNext), progress: Math.min(100, Math.max(0, progress)) }
  }

  const { nextTier, pointsToNext, progress } = getNextTierInfo()

  useEffect(() => {
    const timer = setTimeout(() => {
      setAnimatedProgress(progress)
    }, 300)
    
    return () => clearTimeout(timer)
  }, [progress])

  // Load transactions when component mounts or refresh is triggered
  useEffect(() => {
    if (userData?.user?.external_id) {
      loadTransactions()
    }
  }, [userData?.user?.external_id, refreshTimestamp])

  const loadTransactions = async () => {
    if (!userData?.user?.external_id) return

    // Prevent concurrent loads
    if (loadingTransactionsRef.current) {
      console.log('Already loading transactions, skipping duplicate request')
      return
    }

    loadingTransactionsRef.current = true

    try {
      const timelineData = await getUserTimeline(userData.user.external_id)
      const events = timelineData.result || []
      
      // Find purchase transactions
      const purchaseEvents = events.filter((event: any) => 
        event.event_stream_payload?.event_type_slug === 'PURCHASE_TRANSACTION'
      )
      
      const excludedItems = ['pack', 'package', 'membership', 'classes', 'gift', 'gift card', 'no show']
      
      const processedTransactions = purchaseEvents.map((purchase: any) => {
        const eventData = purchase.event_stream_payload || {}
        return {
          transaction_id: eventData.id,
          pos_transaction_id: eventData.pos_transaction_id || null,
          date: new Date(purchase.timestamp).toLocaleDateString(),
          subtotal: eventData.subtotal || 0,
          store_name: eventData.store?.name || 'Barry\'s Bootcamp',
          items: eventData.items || []
        }
      }).filter((transaction: any) => {
        // Filter out transactions with subtotal <= 0
        if (transaction.subtotal <= 0) return false
        
        // Filter out transactions containing excluded items
        const hasExcludedItem = transaction.items.some((item: any) => {
          const displayName = (item.item_pos?.display_name || '').toLowerCase()
          return excludedItems.some(excludedItem => displayName.includes(excludedItem))
        })
        
        return !hasExcludedItem
      })

      setTransactions(processedTransactions)
    } catch (error) {
      console.error('Failed to load transactions:', error)
    } finally {
      loadingTransactionsRef.current = false
    }
  }

  // Format currency helper function
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount)
  }

  // Get tier promotion/demotion options
  const getTierMoveOptions = () => {
    if (!userData || !userData.user) {
      return { canPromote: false, canDemote: false, promoteToTier: null, demoteToTier: null }
    }

    const tier = userData.user.tier || 'Member'
    const tierOrder = ['Member', 'Red', 'Silver', 'Black']
    const currentIndex = tierOrder.findIndex(t => t.toLowerCase() === tier.toLowerCase())

    const canPromote = currentIndex >= 0 && currentIndex < tierOrder.length - 1
    const canDemote = currentIndex > 0

    const promoteToTier = canPromote ? tierOrder[currentIndex + 1] : null
    const demoteToTier = canDemote ? tierOrder[currentIndex - 1] : null

    return { canPromote, canDemote, promoteToTier, demoteToTier }
  }

  const { canPromote, canDemote, promoteToTier, demoteToTier } = getTierMoveOptions()

  const handleTierMove = async (direction: 'promote' | 'demote') => {
    if (!user || isTierMoving) return

    setIsTierMoving(true)
    setMessage(null)

    try {
      const targetTier = direction === 'promote' ? promoteToTier : demoteToTier
      if (!targetTier) return

      const tierLevelId = TIER_LEVEL_IDS[targetTier.toUpperCase() as keyof typeof TIER_LEVEL_IDS]
      
      await moveTier(user.id, tierLevelId, applyTierRewards)

      // Log audit event
      await logEvent({
        tabName: 'The Academy',
        action: direction === 'promote' ? AUDIT_ACTIONS.TIER_PROMOTED : AUDIT_ACTIONS.TIER_DEMOTED,
        details: {
          from_tier: user.tier || 'Member',
          to_tier: targetTier,
          tier_level_id: tierLevelId,
          process_intermediate_levels: applyTierRewards
        },
        targetUserId: user.id,
        targetUserEmail: userData.user?.email
      })

      setMessage({ 
        type: 'success', 
        text: `Successfully ${direction === 'promote' ? 'promoted' : 'demoted'} user to ${targetTier} tier`
      })

      // Update user data after a brief delay to allow tier change to propagate
      setTimeout(() => {
        onUpdate()
      }, 2000)

    } catch (err) {
      setMessage({ 
        type: 'error', 
        text: err instanceof Error ? err.message : `Failed to ${direction} tier` 
      })
    } finally {
      setIsTierMoving(false)
    }
  }

  const handleEnrollment = async () => {
    if (!user || isEnrolling) return
    
    setIsEnrolling(true)
    setMessage(null)
    
    try {
      // Step 1: Set opted_in: true and loyalty_status: true in SessionM
      await updateUserLoyaltyStatus(user.id)

      // Step 2: Deposit 1 point to opt-in account (triggers automatic tier qualifying)
      await depositOptInPoint(user.id)

      // Log audit event
      await logEvent({
        tabName: 'The Academy',
        action: AUDIT_ACTIONS.ACADEMY_ENROLLMENT,
        details: {
          previous_loyalty_status: false,
          new_loyalty_status: true,
          points_issued: 1
        },
        targetUserId: user.id,
        targetUserEmail: user.email
      })
      
      setMessage({ type: 'success', text: 'Successfully enrolled into The Academy!' })
      
      // Refresh user data
      if (onUpdate) {
        onUpdate()
      }
    } catch (error) {
      console.error('Academy enrollment error:', error)
      setMessage({ type: 'error', text: 'Failed to enroll into The Academy. Please try again.' })
    } finally {
      setIsEnrolling(false)
    }
  }

  const handleSyncTier = async () => {
    if (!user || isSyncingTier) return

    setIsSyncingTier(true)
    setMessage(null)

    try {
      const sessionMId = userData.user.id // SessionM user ID
      const currentTier = userData.user.tier || 'Member'

      await syncTierToMarianaTek(sessionMId, currentTier)

      setMessage({ type: 'success', text: `Successfully synced ${currentTier} tier to Mariana Tek` })
    } catch (error) {
      console.error('Sync tier error:', error)
      setMessage({ type: 'error', text: error instanceof Error ? error.message : 'Failed to sync tier to Mariana Tek' })
    } finally {
      setIsSyncingTier(false)
    }
  }

  if (!userData || !userData.user) {
    return null
  }

  const user = userData.user
  const tier = user.tier || 'Member'
  const tierPoints = user.tier_points || 0
  
  // Check if user is in AUX_Non loyalty tier (needs enrollment)
  const isAuxNonLoyalty = tier === 'AUX_Non loyalty'

  const checkTierThreshold = (currentPoints: number, newPoints: number) => {
    const tierThresholds = [1000, 2500, 6000] // Red, Silver, Black thresholds
    
    for (const threshold of tierThresholds) {
      // Check if we crossed a threshold (either direction)
      if ((currentPoints < threshold && newPoints >= threshold) || 
          (currentPoints >= threshold && newPoints < threshold)) {
        return true
      }
    }
    return false
  }

  const handlePointsAction = async (action: 'deposit' | 'spend' | 'subtract') => {
    if (!amount || isNaN(Number(amount)) || Number(amount) <= 0) {
      setMessage({ type: 'error', text: 'Please enter a valid amount' })
      return
    }

    setIsLoading(true)
    setMessage(null)

    try {
      const pointAmount = Number(amount)
      const currentPoints = tierPoints
      
      // Calculate expected new points based on action
      let expectedNewPoints = currentPoints
      if (action === 'deposit') {
        expectedNewPoints = currentPoints + pointAmount
      } else if (action === 'spend' || action === 'subtract') {
        expectedNewPoints = currentPoints - pointAmount
      }
      
      // Check if we might cross a tier threshold
      const mightCrossTier = checkTierThreshold(currentPoints, expectedNewPoints)
      
      if (action === 'deposit') {
        await depositPoints(user.id, pointSource, pointAmount, selectedTransaction || undefined)
        
        // Log audit event
        await logEvent({
          tabName: 'The Academy',
          action: AUDIT_ACTIONS.POINTS_DEPOSITED,
          details: {
            point_source: pointSource,
            amount: pointAmount,
            previous_points: currentPoints,
            expected_new_points: expectedNewPoints
          },
          targetUserId: user.id,
          targetUserEmail: userData.user?.email
        })
      } else if (action === 'spend') {
        await spendPoints(user.id, pointSource, pointAmount)
        
        // Log audit event
        await logEvent({
          tabName: 'The Academy',
          action: AUDIT_ACTIONS.POINTS_SPENT,
          details: {
            point_source: pointSource,
            amount: pointAmount,
            previous_points: currentPoints,
            expected_new_points: expectedNewPoints
          },
          targetUserId: user.id,
          targetUserEmail: userData.user?.email
        })
      } else if (action === 'subtract') {
        await subtractPoints(user.id, pointSource, pointAmount)
        
        // Log audit event
        await logEvent({
          tabName: 'The Academy',
          action: AUDIT_ACTIONS.POINTS_SUBTRACTED,
          details: {
            point_source: pointSource,
            amount: pointAmount,
            previous_points: currentPoints,
            expected_new_points: expectedNewPoints
          },
          targetUserId: user.id,
          targetUserEmail: userData.user?.email
        })
      }
      
      setAmount('')
      setAnimatedProgress(0) // Reset animation for next update
      
      // Set success message after API call
      setMessage({ 
        type: 'success', 
        text: `Successfully ${action === 'deposit' ? 'deposited' : action === 'spend' ? 'deducted' : 'subtracted'} ${pointAmount} points`
      })
      
      // If we might have crossed a tier threshold, do multiple lookups to catch tier changes
      if (mightCrossTier) {
        setIsCheckingTier(true)

        // Initial update (await to prevent race conditions)
        try {
          await onUpdate()
        } catch (err) {
          console.error('Initial update failed:', err)
        }

        // Multiple retries with increasing delays to catch tier promotions
        setTimeout(async () => {
          try {
            await onUpdate()
          } catch (err) {
            console.error('Update failed at 3s:', err)
          }
        }, 3000)  // 3 seconds

        setTimeout(async () => {
          try {
            await onUpdate()
          } catch (err) {
            console.error('Update failed at 6s:', err)
          }
        }, 6000)  // 6 seconds

        setTimeout(async () => {
          try {
            await onUpdate() // Final update
          } catch (err) {
            console.error('Final update failed:', err)
          } finally {
            setIsCheckingTier(false) // Stop checking after 10 seconds
          }
        }, 10000) // 10 seconds
      } else {
        // Regular update (await to ensure it completes)
        try {
          await onUpdate()
        } catch (err) {
          console.error('Update failed:', err)
        }
      }
    } catch (err) {
      setMessage({ 
        type: 'error', 
        text: err instanceof Error ? err.message : `Failed to ${action} points` 
      })
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <AcademyContainer>
      <AcademyHeader>
        <AcademyMainContent>
          <Title>The Academy</Title>
        </AcademyMainContent>
        <HeaderActions>
          <UserNotesButton onClick={() => setShowUserNotes(true)}>
            User Notes
          </UserNotesButton>
          <TierHistory userData={userData} isSuperAdmin={isSuperAdmin} />
        </HeaderActions>
      </AcademyHeader>

      <TierSection>
        <TierInfo>
          <TierBadge tier={tier}>{tier}</TierBadge>
          <PointsInfo>{tierPoints.toLocaleString()} points</PointsInfo>
          {isAdmin && (
            <TierMoveButtons>
              {canDemote && (
                <TierMoveButton
                  className="demote"
                  onClick={() => handleTierMove('demote')}
                  disabled={isTierMoving}
                >
                  {isTierMoving ? 'Moving...' : `Demote to ${demoteToTier}`}
                </TierMoveButton>
              )}
              {canPromote && (
                <TierMoveButton
                  className="promote"
                  onClick={() => handleTierMove('promote')}
                  disabled={isTierMoving}
                >
                  {isTierMoving ? 'Moving...' : `Promote to ${promoteToTier}`}
                </TierMoveButton>
              )}
              <TierMoveButton
                className="promote"
                onClick={handleSyncTier}
                disabled={isSyncingTier || isTierMoving}
              >
                {isSyncingTier ? 'Syncing...' : 'Sync Tier to Mariana Tek'}
              </TierMoveButton>

              {/* Checkbox directly next to buttons */}
              {(canPromote || canDemote) && (
                <CheckboxContainer>
                  <Checkbox
                    type="checkbox"
                    id="applyTierRewards"
                    checked={applyTierRewards}
                    onChange={(e) => setApplyTierRewards(e.target.checked)}
                    disabled={isTierMoving}
                  />
                  <CheckboxLabel htmlFor="applyTierRewards">
                    Apply tier rewards
                  </CheckboxLabel>
                </CheckboxContainer>
              )}
            </TierMoveButtons>
          )}
        </TierInfo>
        
        {/* Show enrollment button for AUX_Non loyalty users */}
        {isAuxNonLoyalty && (
          <div style={{ marginBottom: '2rem' }}>
            <EnrollButton
              onClick={handleEnrollment}
              disabled={isEnrolling}
            >
              {isEnrolling ? 'Enrolling...' : 'Enroll into The Academy'}
            </EnrollButton>
          </div>
        )}
        
        {nextTier && pointsToNext > 0 && (
          <ProgressContainer>
            <ProgressLabel>
              {pointsToNext.toLocaleString()} points to {nextTier}
            </ProgressLabel>
            <ProgressBar>
              <ProgressFill progress={animatedProgress} />
            </ProgressBar>
          </ProgressContainer>
        )}
      </TierSection>

      <ActionsSection>
        <ActionsTitle>Points Management</ActionsTitle>
        <ActionsForm onSubmit={(e) => e.preventDefault()}>
          <FormGroup>
            <Label htmlFor="pointSource">Type of Action</Label>
            <Select
              id="pointSource"
              value={pointSource}
              onChange={(e) => setPointSource(e.target.value)}
            >
              {Object.keys(POINT_SOURCES).map(source => (
                <option key={source} value={source}>{source}</option>
              ))}
            </Select>
          </FormGroup>

          <FormGroup>
            <Label htmlFor="amount">Amount</Label>
            <Input
              id="amount"
              type="number"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="Enter points"
              min="1"
            />
          </FormGroup>

          <FormGroup>
            <Label htmlFor="transaction">Link this deposit to a transaction</Label>
            <Select
              id="transaction"
              value={selectedTransaction}
              onChange={(e) => setSelectedTransaction(e.target.value)}
            >
              <option value="">Select a transaction (optional)</option>
              {transactions.map((transaction) => (
                <option key={transaction.transaction_id} value={transaction.transaction_id}>
                  {transaction.pos_transaction_id ? `Order #${transaction.pos_transaction_id}` : `Transaction ${transaction.transaction_id}`} - {transaction.date} - ${formatCurrency(transaction.subtotal)}
                </option>
              ))}
            </Select>
          </FormGroup>

          <ActionButtons>
            <GreenButton 
              onClick={() => handlePointsAction('deposit')}
              disabled={isLoading || !amount}
            >
              {isLoading ? 'Processing...' : 'Deposit Points'}
            </GreenButton>
            <RedButton 
              onClick={() => handlePointsAction('subtract')}
              disabled={isLoading || !amount}
            >
              {isLoading ? 'Processing...' : 'Subtract Points'}
            </RedButton>
          </ActionButtons>
        </ActionsForm>

        {message && (
          message.type === 'success' ? 
            <SuccessMessage>{message.text}</SuccessMessage> :
            <ErrorMessage>{message.text}</ErrorMessage>
        )}
        
        {isCheckingTier && (
          <TierCheckingMessage>
            <SpinnerIcon />
            Checking for tier promotions...
          </TierCheckingMessage>
        )}
      </ActionsSection>
      
      <PointLog userData={userData} />

      <UserNotesModal
        isOpen={showUserNotes}
        onClose={() => setShowUserNotes(false)}
        userData={userData}
      />
    </AcademyContainer>
  )
}

export default AcademySection